import React from "react";
import { useQuery } from "@tanstack/react-query";

import { AlertTriangle, Shield } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { DetailOverlay } from "@/components/ui/detail-overlay";
import { Skeleton } from "@/components/ui/skeleton";
import {
  fetchRawWindow,
  telemetryErrorCode,
  type RawFrameRow,
  type TelemetryEvent,
} from "@/lib/telemetry";
import { eventDetailText, eventRefString } from "@/components/telemetry/EventsTable";
import { QueryCost } from "@/components/telemetry/QueryCost";
import { formatNumber, formatTs, toIsoZ } from "@/components/telemetry/format";
import { formatEnergy, formatNumber as formatFixed } from "@voltie/localization-web";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

/** ±2 min around the event: enough to see the unit react, far under the 60-min cap. */
const CONTEXT_MINUTES = 2;

type RawMode = "window" | "frame";

/**
 * Row detail, and — for an admin — the raw protocol frames around it.
 *
 * Raw frames are an internal interface, not tenant-visible data, and they are
 * the single largest column in the lake. So: admin only, a narrow window, an
 * explicit click (never a prefetch), and nothing kept once this closes. The
 * frames are never written to the console or to any log.
 */
export function EventContextDrawer({
  open,
  unitId,
  event,
  tz,
  isAdmin,
  onClose,
}: {
  open: boolean;
  unitId: string;
  event: TelemetryEvent | null;
  tz: "local" | "utc";
  isAdmin: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [mode, setMode] = React.useState<RawMode | null>(null);

  // A new row means a new question — never show the previous row's frames.
  React.useEffect(() => setMode(null), [event?.ts, event?.ref?.src_line]);

  const ref = event ? eventRefString(event) : null;
  const eventTime = event ? new Date(event.ts).getTime() : 0;
  const from = toIsoZ(new Date(eventTime - CONTEXT_MINUTES * 60_000));
  const to = toIsoZ(new Date(eventTime + CONTEXT_MINUTES * 60_000));

  const rawQ = useQuery({
    queryKey: ["telemetry", "raw-window", unitId, event?.ts, event?.ref?.src_line, mode],
    queryFn: () =>
      fetchRawWindow(unitId, {
        from,
        to,
        refs: mode === "frame" && ref ? [ref] : undefined,
        include_raw: true,
        limit: mode === "frame" ? 5 : 200,
        order: "asc",
      }),
    enabled: open && isAdmin && mode !== null && !!event,
    // Raw frames live exactly as long as this overlay does.
    gcTime: 0,
    staleTime: 0,
    // A retry is another paid scan, and this one carries the expensive column.
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (!open || !event) return null;

  const frames = rawQ.data?.data ?? [];
  const forbidden = telemetryErrorCode(rawQ.error) === "raw_forbidden";

  return (
    <DetailOverlay
      open={open}
      onClose={onClose}
      title={t("operator.telemetry.drawer_title", {
        kind: t("operator.telemetry.kind_name", { kind: event.kind, raw: event.kind }),
        time: formatTs(event.ts, tz),
      })}
      className="max-w-4xl"
    >
      <div className="space-y-4" data-testid="telemetry-event-drawer">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
          <Field label={t("operator.telemetry.time_local")} value={formatTs(event.ts, "local")} />
          <Field label={t("operator.telemetry.column_time", { zone: "utc" })} value={formatTs(event.ts, "utc")} />
          <Field label={t("operator.telemetry.column_state")} value={event.state ?? "—"} />
          <Field label={t("operator.telemetry.column_ref")} value={ref ?? "—"} mono />
          <Field label={t("operator.metric.voltage")} value={`${formatNumber(event.voltage)} V`} />
          <Field label={t("operator.metric.current")} value={`${formatNumber(event.current)} A`} />
          <Field label={t("operator.metric.temperature")} value={event.temperature != null ? `${event.temperature} °C` : "—"} />
          <Field label={t("operator.metric.frequency")} value={`${formatNumber(event.frequency)} Hz`} />
          <Field
            label={t("operator.telemetry.session_energy")}
            value={event.session_energy != null ? formatEnergy(event.session_energy, "Wh", "summary") : "—"}
          />
          <Field
            label={t("operator.telemetry.lifetime_energy")}
            value={event.lifetime_energy != null ? `${formatFixed(event.lifetime_energy / 1000, 1)} kWh` : "—"}
          />
        </dl>

        <div>
          <div className="text-[11px] text-muted-foreground">{t("operator.telemetry.detail")}</div>
          <div className="mt-1 text-sm">{eventDetailText(t, event) ?? "—"}</div>
          {event.detail && (
            <pre className="mt-2 max-h-40 overflow-auto rounded-lg border border-border bg-muted/30 p-2 font-mono text-[11px]">
              {JSON.stringify(event.detail, null, 2)}
            </pre>
          )}
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Shield className="h-4 w-4" /> {t("operator.telemetry.raw_frames_title")}
            </span>
            {isAdmin ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="raw-context-window"
                  onClick={() => setMode("window")}
                  disabled={rawQ.isFetching && mode === "window"}
                >
                  {t("operator.telemetry.raw_context", { minutes: CONTEXT_MINUTES })}
                </Button>
                {ref && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="raw-context-frame"
                    onClick={() => setMode("frame")}
                    disabled={rawQ.isFetching && mode === "frame"}
                  >
                    {t("operator.telemetry.raw_this_frame")}
                  </Button>
                )}
                <span className="text-[11px] text-muted-foreground">
                  {t("operator.telemetry.raw_scan_note")}
                </span>
              </>
            ) : (
              <span
                className="text-xs text-muted-foreground"
                data-testid="raw-forbidden-note"
              >
                {t("operator.telemetry.raw_admin_only")}
              </span>
            )}
          </div>

          {isAdmin && mode !== null && (
            <div className="mt-3">
              {rawQ.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-2/3" />
                </div>
              ) : rawQ.isError ? (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-xs text-destructive">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {forbidden
                      ? t("operator.telemetry.raw_forbidden")
                      : errorMessage(t, rawQ.error, { family: "telemetry_request", fallback: "operator.telemetry.query_failed_fallback" })}
                  </span>
                </div>
              ) : frames.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  {t("operator.telemetry.frames_empty")}
                </div>
              ) : (
                <RawFrames frames={frames} tz={tz} />
              )}

              {rawQ.data && (
                <div className="mt-2">
                  <QueryCost
                    queries={[rawQ.data.meta.query]}
                    budget={rawQ.data.meta.budget}
                    truncated={rawQ.data.meta.truncated}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DetailOverlay>
  );
}

function RawFrames({ frames, tz }: { frames: RawFrameRow[]; tz: "local" | "utc" }) {
  const t = useT();
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-border">
      <table className="w-full text-left text-[11px]">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b border-border">
            <th className="px-2 py-1 font-medium">{t("operator.telemetry.column_time", { zone: tz })}</th>
            <th className="px-2 py-1 font-medium">{t("operator.telemetry.column_state")}</th>
            <th className="px-2 py-1 font-medium">{t("operator.telemetry.column_volts_amps")}</th>
            <th className="px-2 py-1 font-medium">{t("operator.telemetry.column_frame")}</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {frames.map((frame, index) => (
            <tr key={`${frame.src_file}:${frame.src_line}:${index}`} className="border-b border-border/50 align-top">
              <td className="whitespace-nowrap px-2 py-1">{formatTs(frame.ts_utc, tz)}</td>
              <td className="px-2 py-1">{frame.state ?? "—"}</td>
              <td className="whitespace-nowrap px-2 py-1">
                {formatNumber(frame.voltage)} / {formatNumber(frame.current)}
              </td>
              <td className="px-2 py-1">
                <span className="block max-w-[52ch] break-all whitespace-pre-wrap">
                  {frame.raw_message ?? "—"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className={`truncate ${mono ? "font-mono" : ""}`} title={typeof value === "string" ? value : undefined}>
        {value}
      </dd>
    </div>
  );
}
